import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../lib/use-api-client.js';
import type { ClientDto } from './use-clients.js';

/**
 * TanStack Query hook for `GET /v1/clients/{id}`. Disabled until both
 * the user is signed in (`useApiClient` returns `undefined` pre-auth)
 * AND an `id` has been provided. The route param is undefined on
 * mount under react-router until the path resolves, so guarding here
 * keeps the query from firing with `/v1/clients/undefined`.
 *
 * Casbin gates `(client, read)` on the server — same policy as the
 * list endpoint. Unknown roles get 403, surfacing as `ApiError`.
 */
export function useClientQuery(id: string | undefined): UseQueryResult<ClientDto> {
  const api = useApiClient();
  return useQuery({
    queryKey: ['clients', id],
    queryFn: () => {
      if (!api) {
        throw new Error('useClientQuery: api client unavailable (no signed-in account)');
      }
      if (!id) {
        throw new Error('useClientQuery: id is required');
      }
      return api.get<ClientDto>(`/v1/clients/${id}`);
    },
    enabled: api !== undefined && id !== undefined,
  });
}
